import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { Header } from '../Layout/Header'
import {
  CheckCircle, Circle, Trash2, Plus, Clock, AlertTriangle, X,
  Forklift, Warehouse, Wrench, RefreshCw,
} from 'lucide-react'

interface Task {
  id: string
  task: string
  completed: boolean
  scheduled_at?: string
  created_at: string
  created_by: string
  created_by_name: string
  assigned_to?: string
  assigned_to_name?: string
  completed_at?: string
  priority?: string
}

interface Machine {
  id: string
  name: string
  status: string
  hal?: string
  location?: string
}

interface Problem {
  id: string
  machine_id?: string
  datum_tijd: string
  argumentatie?: string
  oplossing_gevonden: boolean
}

const STATUS_META: Record<string, { label: string; dot: string; badge: string }> = {
  active:      { label: 'In bedrijf',  dot: 'bg-green-500',  badge: 'badge badge-green' },
  maintenance: { label: 'Onderhoud',   dot: 'bg-orange-500', badge: 'badge badge-yellow' },
  inactive:    { label: 'Buiten dienst', dot: 'bg-gray-400', badge: 'badge badge-gray' },
}

const statusMeta = (status?: string) => STATUS_META[status || 'active'] ?? STATUS_META.active

export const ForkliftDashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [tasks, setTasks] = useState<Task[]>([])
  const [machines, setMachines] = useState<Machine[]>([])
  const [problems, setProblems] = useState<Problem[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Nieuwe taak
  const [showTaskForm, setShowTaskForm] = useState(false)
  const [newTask, setNewTask] = useState('')
  const [scheduledTime, setScheduledTime] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<'laag' | 'normaal' | 'hoog'>('normaal')

  // Probleem melden
  const [showProblemModal, setShowProblemModal] = useState(false)
  const [problemForm, setProblemForm] = useState({ productcode: '', machine_id: '', argumentatie: '' })
  const [problemSubmitting, setProblemSubmitting] = useState(false)
  const [problemSuccess, setProblemSuccess] = useState(false)
  const [problemError, setProblemError] = useState<string | null>(null)

  const loadAll = async () => {
    if (!user?.id) return
    const [taskRes, machineRes, problemRes] = await Promise.all([
      supabase
        .from('tasks').select('*')
        .or(`assigned_to.eq.${user.id},created_by.eq.${user.id}`)
        .order('created_at', { ascending: false }),
      supabase.from('machines').select('id, name, status, hal, location').order('name'),
      supabase.from('probleem_meldingen').select('id, machine_id, datum_tijd, argumentatie, oplossing_gevonden')
        .eq('oplossing_gevonden', false),
    ])
    if (!taskRes.error) setTasks(taskRes.data || [])
    if (!machineRes.error) setMachines(machineRes.data || [])
    if (!problemRes.error) setProblems(problemRes.data || [])
    setLoading(false)
  }

  useEffect(() => {
    loadAll()
    const channel = supabase.channel('forklift-dashboard')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => loadAll())
      .subscribe()
    return () => { channel.unsubscribe() }
  }, [user?.id])

  const refresh = async () => {
    setRefreshing(true)
    await loadAll()
    setRefreshing(false)
  }

  const addTask = async () => {
    if (!newTask.trim()) return
    const { error } = await supabase.from('tasks').insert([{
      task: newTask.trim(),
      completed: false,
      scheduled_at: scheduledTime ? new Date(`${new Date().toDateString()} ${scheduledTime}`).toISOString() : null,
      created_by: user?.id,
      created_by_name: user?.name || 'Onbekend',
      assigned_to: user?.id,
      assigned_to_name: user?.name,
      priority: newTaskPriority,
    }])
    if (!error) {
      setNewTask('')
      setScheduledTime('')
      setNewTaskPriority('normaal')
      setShowTaskForm(false)
      loadAll()
    }
  }

  const toggleTask = async (taskId: string, currentStatus: boolean) => {
    await supabase.from('tasks').update({
      completed: !currentStatus,
      completed_at: !currentStatus ? new Date().toISOString() : null,
    }).eq('id', taskId)
    loadAll()
  }

  const deleteTask = async (taskId: string) => {
    await supabase.from('tasks').delete().eq('id', taskId)
    loadAll()
  }

  const submitProblem = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!problemForm.argumentatie.trim()) return
    setProblemSubmitting(true)
    setProblemError(null)
    try {
      const { error } = await supabase.from('probleem_meldingen').insert([{
        datum_tijd: new Date().toISOString(),
        productcode: problemForm.productcode || null,
        machine_id: problemForm.machine_id || null,
        argumentatie: problemForm.argumentatie.trim(),
        oplossing_gevonden: false,
        gebruiker_naam: user?.name || 'Onbekend',
        created_by: user?.id || 'unknown',
        created_by_name: user?.name || 'Onbekend',
      }])
      if (error) {
        setProblemError(error.message || 'Melden mislukt, probeer het opnieuw.')
        return
      }
      setProblemSuccess(true)
      setProblemForm({ productcode: '', machine_id: '', argumentatie: '' })
      loadAll()
      setTimeout(() => { setProblemSuccess(false); setShowProblemModal(false) }, 1800)
    } finally {
      setProblemSubmitting(false)
    }
  }

  const handleLogout = () => { logout(); navigate('/login') }

  // ─── Afgeleide waarden ──────────────────────────────────────────────
  const pendingTasks = tasks.filter(t => !t.completed)
  const completedTasks = tasks.filter(t => t.completed)
  const urgentTasks = pendingTasks.filter(t => t.priority === 'hoog')
  const today = new Date().toISOString().split('T')[0]
  const completedToday = completedTasks.filter(t => t.completed_at?.startsWith(today)).length

  const priorityRank: Record<string, number> = { hoog: 0, normaal: 1, laag: 2 }
  const sortedPending = [...pendingTasks].sort(
    (a, b) => (priorityRank[a.priority || 'normaal'] ?? 1) - (priorityRank[b.priority || 'normaal'] ?? 1)
  )

  const problemsByMachine = problems.reduce<Record<string, number>>((acc, p) => {
    if (p.machine_id) acc[p.machine_id] = (acc[p.machine_id] || 0) + 1
    return acc
  }, {})

  const halls = [...new Set(machines.map(m => m.hal || 'Overig'))].sort()
  const blockedMachines = machines.filter(m => m.status !== 'active')

  const getPriorityStyle = (priority?: string) => {
    switch (priority) {
      case 'hoog': return 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/10'
      case 'laag': return 'border-l-4 border-l-gray-300 bg-gray-50 dark:bg-gray-800'
      default:     return 'border-l-4 border-l-yellow-400 bg-yellow-50 dark:bg-yellow-900/10'
    }
  }

  const getPriorityBadge = (priority?: string) => {
    if (priority === 'hoog') return <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full dark:bg-red-900/40 dark:text-red-300">Urgent</span>
    if (priority === 'laag') return <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full dark:bg-gray-700 dark:text-gray-400">Laag</span>
    return null
  }

  const kpis = [
    { label: 'Open ritten/taken', value: pendingTasks.length,   icon: Forklift,      tone: 'amber' },
    { label: 'Urgent',            value: urgentTasks.length,    icon: AlertTriangle, tone: 'red' },
    { label: 'Klaar vandaag',     value: completedToday,        icon: CheckCircle,   tone: 'green' },
    { label: 'Machines stil',     value: blockedMachines.length, icon: Wrench,       tone: 'blue' },
  ]

  return (
    <div className="min-h-screen flex flex-col items-center overflow-x-hidden" style={{ background: 'var(--bg)' }}>
      <Header title="Heftruck dashboard" onLogout={handleLogout} />

      <section className="w-full max-w-5xl px-3 sm:px-6 mt-6 sm:mt-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="eyebrow">Logistiek · heftruck</p>
            <h2 className="page-title mt-2">Goedendag, {user?.name?.split(' ')[0] || 'collega'}</h2>
            <p className="page-subtitle mt-1">Je ritten voor vandaag en de actuele staat van het machinepark.</p>
          </div>
          <button onClick={refresh} className="btn-secondary touch-target" disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} /> Vernieuwen
          </button>
        </div>
      </section>

      {/* KPI's */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-5">
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-3">
          {kpis.map(kpi => {
            const Icon = kpi.icon
            return (
              <div key={kpi.label} className="metric-card">
                <div className={`metric-icon metric-icon-${kpi.tone}`}><Icon size={18} /></div>
                <div className="min-w-0">
                  <div className="text-[11px] sm:text-xs font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{kpi.label}</div>
                  <div className="text-2xl sm:text-[28px] leading-tight font-semibold tracking-[-0.03em] mt-1" style={{ color: 'var(--text-primary)' }}>
                    {loading ? '–' : kpi.value}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Waarschuwingen */}
      {(urgentTasks.length > 0 || blockedMachines.length > 0) && (
        <div className="w-full max-w-5xl px-3 sm:px-6 mt-3 space-y-2">
          {urgentTasks.length > 0 && (
            <div className="bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded-xl flex items-center gap-2 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
              <AlertTriangle size={18} />
              <span className="font-semibold text-sm">
                {urgentTasks.length} urgente {urgentTasks.length === 1 ? 'rit' : 'ritten'} vereist je aandacht
              </span>
            </div>
          )}
          {blockedMachines.length > 0 && (
            <div className="bg-orange-100 border border-orange-300 text-orange-900 px-4 py-3 rounded-xl flex items-start gap-2 dark:bg-orange-900/25 dark:border-orange-800 dark:text-orange-200">
              <Wrench size={18} className="mt-0.5 shrink-0" />
              <span className="text-sm">
                Niet in bedrijf: {blockedMachines.map(m => m.name).join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Takenlijst */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4">
        <section className="card p-4 sm:p-6">
          <div className="flex justify-between items-center border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Mijn ritten &amp; taken ({pendingTasks.length} open)
            </h2>
            <button onClick={() => setShowTaskForm(!showTaskForm)} className="btn-primary !px-3 touch-target">
              <Plus size={16} /> Taak
            </button>
          </div>

          {showTaskForm && (
            <div className="mb-4 p-4 rounded-xl" style={{ background: 'var(--bg)' }}>
              <input
                type="text"
                value={newTask}
                onChange={e => setNewTask(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addTask()}
                placeholder="Bijv. pallets Hal 1 → expeditie"
                className="input-field mb-2"
              />
              <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)} className="input-field mb-3" />
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Prioriteit</label>
              <div className="flex rounded-lg overflow-hidden border mb-3" style={{ borderColor: 'var(--border)' }}>
                {([
                  { val: 'laag' as const, label: 'Laag', activeClass: 'bg-gray-500 text-white' },
                  { val: 'normaal' as const, label: 'Normaal', activeClass: 'bg-yellow-500 text-white' },
                  { val: 'hoog' as const, label: 'Hoog', activeClass: 'bg-red-600 text-white' },
                ]).map(opt => (
                  <button key={opt.val} type="button" onClick={() => setNewTaskPriority(opt.val)}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                      newTaskPriority === opt.val ? opt.activeClass : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setShowTaskForm(false); setNewTask(''); setScheduledTime(''); setNewTaskPriority('normaal') }}
                  className="btn-secondary flex-1 touch-target">Annuleren</button>
                <button onClick={addTask} className="btn-primary flex-1 touch-target">Toevoegen</button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {loading ? (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>Laden...</div>
            ) : sortedPending.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                <CheckCircle size={48} className="mx-auto mb-2 text-green-400" />
                <p>Geen openstaande ritten.</p>
              </div>
            ) : (
              sortedPending.map(task => (
                <div key={task.id} className={`p-3 rounded-xl transition-all hover:shadow-md ${getPriorityStyle(task.priority)}`}>
                  <div className="flex items-start gap-3">
                    <button onClick={() => toggleTask(task.id, task.completed)}
                      className="text-gray-400 hover:text-green-500 mt-0.5 touch-target flex items-center justify-center"
                      aria-label="Taak afronden">
                      <Circle size={26} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{task.task}</span>
                        {getPriorityBadge(task.priority)}
                        {task.created_by !== user?.id && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full dark:bg-blue-900/40 dark:text-blue-300">
                            Van: {task.created_by_name}
                          </span>
                        )}
                      </div>
                      <div className="text-sm mt-1 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-secondary)' }}>
                        {task.scheduled_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {new Date(task.scheduled_at).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        <span>{new Date(task.created_at).toLocaleDateString('nl-NL')}</span>
                      </div>
                    </div>
                    <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500 touch-target flex items-center justify-center" aria-label="Taak verwijderen">
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {completedTasks.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm py-2" style={{ color: 'var(--text-secondary)' }}>
                {completedTasks.length} voltooide {completedTasks.length === 1 ? 'taak' : 'taken'} tonen
              </summary>
              <div className="mt-2 space-y-2">
                {completedTasks.map(task => (
                  <div key={task.id} className="p-3 rounded-xl bg-green-50 border-l-4 border-l-green-400 dark:bg-green-900/10">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggleTask(task.id, task.completed)} className="text-green-500 mt-0.5 touch-target flex items-center justify-center" aria-label="Terugzetten naar open">
                        <CheckCircle size={26} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className="line-through text-gray-500 dark:text-gray-400">{task.task}</span>
                        <div className="text-xs text-gray-400 mt-1">
                          Voltooid: {task.completed_at ? new Date(task.completed_at).toLocaleString('nl-NL') : 'Onbekend'}
                        </div>
                      </div>
                      <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500 touch-target flex items-center justify-center" aria-label="Taak verwijderen">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      </div>

      {/* Machinepark per hal */}
      <div className="w-full max-w-5xl px-3 sm:px-6 mt-4">
        <section className="card p-4 sm:p-6">
          <div className="flex items-center gap-2 border-b pb-3 mb-4" style={{ borderColor: 'var(--border)' }}>
            <Warehouse size={18} style={{ color: 'var(--text-secondary)' }} />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Machinepark</h2>
          </div>

          {machines.length === 0 ? (
            <p className="text-sm py-4" style={{ color: 'var(--text-secondary)' }}>Geen machines gevonden.</p>
          ) : (
            <div className="space-y-4">
              {halls.map(hal => (
                <div key={hal} className="hall-section">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="hall-icon"><Warehouse size={16} /></div>
                    <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{hal}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {machines.filter(m => (m.hal || 'Overig') === hal).map(machine => {
                      const meta = statusMeta(machine.status)
                      const openProblems = problemsByMachine[machine.id] || 0
                      return (
                        <div key={machine.id} className="machine-card">
                          <div className="flex items-start gap-2">
                            <span className={`status-indicator ${meta.dot}`} />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm truncate" style={{ color: 'var(--text-primary)' }}>{machine.name}</div>
                              {machine.location && (
                                <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                                  {machine.location}
                                </div>
                              )}
                              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                <span className={meta.badge}>{meta.label}</span>
                                {openProblems > 0 && (
                                  <span className="machine-stat machine-stat-alert">
                                    <AlertTriangle size={11} /> {openProblems} storing{openProblems === 1 ? '' : 'en'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Actie */}
      <div className="w-full max-w-5xl px-3 sm:px-6 my-6">
        <button onClick={() => { setShowProblemModal(true); setProblemError(null) }}
          className="btn-primary w-full py-4 text-base font-semibold flex items-center justify-center gap-2">
          <AlertTriangle size={22} /> Probleem melden
        </button>
      </div>

      {/* Probleem-modal */}
      {showProblemModal && (
        <div className="modal-overlay" onClick={() => { setShowProblemModal(false); setProblemSuccess(false) }}>
          <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Probleem melden</h3>
              <button onClick={() => { setShowProblemModal(false); setProblemSuccess(false) }}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition" aria-label="Sluiten">
                <X size={18} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            {problemSuccess ? (
              <div className="text-center py-8">
                <CheckCircle size={48} className="mx-auto mb-2 text-green-500" />
                <p className="font-semibold text-green-700 dark:text-green-400">Probleem gemeld!</p>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>De technische dienst ziet je melding direct.</p>
              </div>
            ) : (
              <form onSubmit={submitProblem} className="space-y-3">
                {problemError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm dark:bg-red-900/25 dark:text-red-300">{problemError}</div>
                )}
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Productcode</label>
                  <input className="input-field" value={problemForm.productcode}
                    onChange={e => setProblemForm(p => ({ ...p, productcode: e.target.value }))}
                    placeholder="Productcode (optioneel)" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Machine</label>
                  <select className="input-field" value={problemForm.machine_id}
                    onChange={e => setProblemForm(p => ({ ...p, machine_id: e.target.value }))}>
                    <option value="">– Selecteer machine –</option>
                    {machines.map(m => (
                      <option key={m.id} value={m.id}>{m.name}{m.hal ? ` (${m.hal})` : ''}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Omschrijving *</label>
                  <textarea required className="input-field resize-none" rows={4} value={problemForm.argumentatie}
                    onChange={e => setProblemForm(p => ({ ...p, argumentatie: e.target.value }))}
                    placeholder="Beschrijf wat er aan de hand is..." />
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={() => setShowProblemModal(false)} className="btn-secondary flex-1 touch-target">Annuleren</button>
                  <button type="submit" disabled={problemSubmitting} className="btn-primary flex-1 touch-target disabled:opacity-50">
                    {problemSubmitting ? 'Melden...' : 'Melding indienen'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
